/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";

import {
  UserTrackingServiceApi,
  V1ExperimentLookupRequest,
  V1ExperimentLookupResponse,
  V1Platform,
} from "@flink/experiment";

import { useFeatureFlagOverridesStore } from "app/stores/useFeatureFlagOverridesStore";
import { EppoFeatureFlags, EppoFeatureVariants } from "app/types/flags";
import { config } from "config";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { isNullOrUndefined } from "utils/tsHelpers";

import { useHubSlug } from "./useHubSlug";

export type AllFeatureFlag = Record<string, string | undefined> | null;

const FALLBACK_FLAGS: AllFeatureFlag = {
  [EppoFeatureFlags.DEVICE_CLAIMING_PROCESS]: EppoFeatureVariants.ON,
};

const transformData = (flags: V1ExperimentLookupResponse["decisions"]): AllFeatureFlag =>
  flags
    ? Object.fromEntries(Object.entries(flags).map(([key, { variation }]) => [key, variation]))
    : null;

export const fetchAllFeatureFlagDecisions = async (hubSlug: string | null) => {
  const userTrackingServiceApi = new UserTrackingServiceApi({}, config.environment.EPPO_URL);
  const featureFlags = Object.values(EppoFeatureFlags);
  const experimentRequest: V1ExperimentLookupRequest = {
    userId: "not-used",
    meta: hubSlug ? { attributes: { hub_slug: hubSlug } } : {},
    flags: featureFlags,
    platform: V1Platform.EppoOps,
  };
  const experimentResponse =
    await userTrackingServiceApi.userTrackingServiceExperimentLookup(experimentRequest);
  return transformData(experimentResponse.data.decisions);
};

export const useEppoAllFeatureFlags = () => {
  const hubSlug = useHubSlug();
  const [decisions, setDecisions] = useState<AllFeatureFlag>(null);
  const setLocalFeatureFlags = useFeatureFlagOverridesStore((state) => state.setLocalFeatureFlags);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAllFeatureFlags = useCallback(async () => {
    setIsLoading(true);
    try {
      const responseDecisions = await fetchAllFeatureFlagDecisions(hubSlug);
      setDecisions(responseDecisions);
      // Storing fetched feature flags as local overrides if available
      if (!isNullOrUndefined(responseDecisions)) setLocalFeatureFlags(responseDecisions);
    } catch (error: any) {
      console.error("Error checking feature flags:", error?.response?.data ?? error);
      // On refetch failure keep current decisions; only fall back to cache on initial load
      setDecisions(
        (prev) =>
          prev ?? {
            ...useFeatureFlagOverridesStore.getState().localFeatureFlags,
            ...FALLBACK_FLAGS,
          },
      );
    } finally {
      setIsLoading(false);
    }
  }, [hubSlug]);

  useEffect(() => {
    fetchAllFeatureFlags();
  }, [fetchAllFeatureFlags]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // Skip auto-refresh while a timed activity is in progress to avoid mid-task flag flips
      if (useTimedActivityStore.getState().timedActivityInstance) return;
      fetchAllFeatureFlags();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchAllFeatureFlags]);

  return { decisions, isLoading, refetch: fetchAllFeatureFlags };
};
